import { DataTable } from '@/components/features/shared/DataTable'
import { OutstandingAmount } from '@/components/features/shared/OutstandingAmount'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { UserRole } from '@/domain/auth/auth'
import { formatParcelState, getParcelStateColor, Parcel, PARCEL_SECTIONS, ParcelContractState, ParcelState } from '@/domain/parcels/parcel'
import { useParcels, useUpdateParcelStatus } from '@/hooks/api/useParcels'
import { useServerPagination } from '@/hooks/useServerPagination'
import { useToast } from '@/hooks/use-toast'
import { Paths } from '@/routes/paths'
import { useAuthStore } from '@/store/useAuthStore'
import { capitalizeString } from '@/utils/strings'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowRight, DollarSign, Edit, Loader2, MapPin, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { PageContainer } from '@/components/features/shared/PageContainer'
import { NavHeader } from '@/components/features/shared/NavHeader'

export const ParcelsManagement = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasAnyRole } = useAuthStore()
  const searchParams = useSearch({ strict: false }) as any

  const selectedSection = searchParams.section || 'all'
  const selectedFila = String(searchParams.fila || 'all')
  const selectedState = (searchParams.state as ParcelState | 'all') || 'all'
  const selectedSellCode = searchParams.sellCode || 'all'
  const selectedExpensesCode = searchParams.expensesCode || 'all'

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedParcel, setSelectedParcel] = useState<Parcel | null>(null)
  const [newStatus, setNewStatus] = useState<string>('')

  const {
    pagination,
    setPagination: handlePaginationChange,
    search,
    inputValue,
    setInputValue,
    handleSearch,
    updateQueryParams,
    handleClearFilters,
    limit,
  } = useServerPagination({
    clearDefaults: {
      section: 'all',
      fila: 'all',
      state: 'all',
      sellCode: 'all',
      expensesCode: 'all',
    },
    resetOn: [selectedSection, selectedFila, selectedState, selectedSellCode, selectedExpensesCode],
  })

  const isEmployee = hasAnyRole([UserRole.Employee])

  const navigateToContract = (contractId: string, parcelId: string) => {
    if (isEmployee) {
      navigate({ to: `${Paths.Management.ParcelContract.replace('$contractId', contractId).replace('$parcelId', parcelId)}` })
    } else {
      navigate({ to: `${Paths.Management.Contracts}/${contractId}` })
    }
  }

  const { data: paginatedResponse, isLoading } = useParcels({
    section: selectedSection,
    fila: selectedFila,
    state: selectedState === 'all' ? undefined : selectedState,
    sellCode: selectedSellCode === 'all' ? undefined : selectedSellCode,
    expensesCode: selectedExpensesCode === 'all' ? undefined : selectedExpensesCode,
    search: search || undefined,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  })

  const updateStatusMutation = useUpdateParcelStatus()

  const handleNewSale = (parcelId: string) => {
    navigate({
      to: `${Paths.Management.ParcelsSell}?parcelId=${parcelId}` as any
    })
  }

  const handleEditStatus = (parcel: Parcel) => {
    setSelectedParcel(parcel)
    setNewStatus(parcel.state)
    setIsEditDialogOpen(true)
  }

  const handleSaveStatus = async () => {
    if (!selectedParcel) return

    await updateStatusMutation.mutateAsync({
      id: selectedParcel.id,
      state: newStatus,
    })
    toast({
      title: 'Estado actualizado',
      description: `La parcela #${selectedParcel.parcela} ha sido actualizada a ${formatParcelState(newStatus as ParcelState)}.`,
      variant: 'success',
    })
    setIsEditDialogOpen(false)
  }

  const columns = useMemo<ColumnDef<Parcel>[]>(() => [
    {
      header: 'Ubicación',
      cell: ({ row }) => {
        const parcel = row.original
        return (
          <div className="flex flex-col gap-2">
            <Badge variant="outline" className="bg-gray-50 font-mono w-[max-content]">
              Sección {parcel.section}
            </Badge>
            <Badge variant="outline" className="bg-gray-50 font-mono w-[max-content]">
              Fila {parcel.fila}
            </Badge>
          </div>
        )
      }
    },
    {
      accessorKey: 'parcela',
      header: 'Parcela',
      cell: ({ row }) => <span className="font-bold">#{row.original.parcela}</span>
    },
    {
      accessorKey: 'sellCode',
      header: 'Plan',
      cell: ({ row }) => <span className="font-bold">{row.original.sellCode}</span>
    },
    {
      accessorKey: 'expensesCode',
      header: 'Expensas',
      cell: ({ row }) => <span className="font-bold">{row.original.expensesCode}</span>
    },
    {
      accessorKey: 'state',
      header: 'Estado',
      cell: ({ row }) => (
        <Badge className={getParcelStateColor(row.original.state, row.original.parcelContractState) + " w-[max-content]"}>
          {formatParcelState(row.original.state, row.original.parcelContractState)}
        </Badge>
      )
    },
    {
      header: 'Propietario / Contrato',
      cell: ({ row }) => {
        const parcel = row.original
        return parcel.contractId ? (
          <div className="flex flex-col">
            <span className="font-medium text-sm">{parcel.ownerName || 'Titular no disponible'}</span>
            <span className="text-sm text-muted-foreground font-medium">{parcel.contractNumber}</span>
          </div>
        ) : (
          <span className="text-muted-foreground italic text-xs">Sin propietario</span>
        )
      }
    },
    {
      accessorKey: 'deceasedNames',
      header: 'Fallecidos',
      maxSize: 50,
      cell: ({ row }) => (
        <div className="flex flex-col">
          {row.original.deceasedNames ? (
            row.original.deceasedNames.split(',').map(n => capitalizeString(n.toLocaleLowerCase())).map((name, index) => (
              <span key={index} className="text-sm">{name}</span>
            ))
          ) : (
            <span className="text-muted-foreground italic text-xs">-</span>
          )}
        </div>
      )
    },
    {
      accessorKey: 'outstandingAmount',
      header: 'Deuda',
      cell: ({ row }) => {
        const isOccupiedOrSold = row.original.state === ParcelState.OCCUPIED || row.original.state === ParcelState.SOLD
        return <OutstandingAmount balance={row.original.outstandingAmount} available={isOccupiedOrSold} />
      }
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Acciones</div>,
      cell: ({ row }) => {
        const parcel = row.original
        return (
          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            {parcel.contractId && (
              <IconButton
                variant="default"
                title='Ver contrato'
                onClick={() => navigateToContract(parcel.contractId!, parcel.id)}
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </IconButton>
            )}
            {!isEmployee && parcel.state === ParcelState.AVAILABLE && (
              <IconButton
                variant="primary"
                title='Vender parcela'
                onClick={() => handleNewSale(parcel.id)}
              >
                <DollarSign className="h-3.5 w-3.5" />
              </IconButton>
            )}
            {!isEmployee && (
              <IconButton
                variant="warning"
                title='Editar estado de parcela'
                onClick={() => handleEditStatus(parcel)}
              >
                <Edit className="h-3.5 w-3.5" />
              </IconButton>
            )}
          </div>
        )
      }
    }
  ], [navigateToContract])

  return (
    <PageContainer>
      <NavHeader hideBackButton>
        <h1 className="text-3xl font-bold tracking-tight">Gestión de Parcelas</h1>
        <p className="text-muted-foreground">
          Visualiza y gestiona las parcelas del cementerio por sección y fila.
        </p>
      </NavHeader>

      <Card>
        <CardContent className='pt-6 grid grid-cols-1 lg:grid-cols-[5fr_1fr] gap-3'>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex flex-col gap-y-2">
              <span className="font-mono text-sm text-muted-foreground font-bold leading-none ml-1">Sección</span>
              <Select value={selectedSection} onValueChange={(val) => updateQueryParams({ section: val })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key={"all"} value={"all"}>
                    Todas
                  </SelectItem>
                  {PARCEL_SECTIONS.map((section) => (
                    <SelectItem key={section} value={section}>
                      {section}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="font-mono text-sm text-muted-foreground font-bold leading-none ml-1">Fila</span>
              <Select value={selectedFila} onValueChange={(val) => updateQueryParams({ fila: val })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key={"all"} value={"all"}>
                    Todas
                  </SelectItem>
                  {Array.from({ length: 13 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="font-mono text-sm text-muted-foreground font-bold leading-none ml-1">Estado</span>
              <Select value={selectedState} onValueChange={(val) => updateQueryParams({ state: val })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value={ParcelState.AVAILABLE}>Disponible</SelectItem>
                  <SelectItem value={ParcelState.OCCUPIED}>Ocupado</SelectItem>
                  <SelectItem value={ParcelState.SOLD}>Vendido</SelectItem>
                  <SelectItem value={ParcelState.RESERVED}>Reservado</SelectItem>
                  <SelectItem value={ParcelState.NOT_AVAILABLE}>No Disponible</SelectItem>
                  <SelectItem value={ParcelContractState.EXHUMATED}>Exhumado</SelectItem>
                  <SelectItem value={ParcelContractState.SUSPENDED}>Suspendido</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="font-mono text-sm text-muted-foreground font-bold leading-none ml-1">Plan</span>
              <Select value={selectedSellCode} onValueChange={(val) => updateQueryParams({ sellCode: val })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="U">Urgencia</SelectItem>
                  <SelectItem value="P">Prenecesidad</SelectItem>
                  <SelectItem value="T">Traslado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="font-mono text-sm text-muted-foreground font-bold leading-none ml-1">Expensas</span>
              <Select value={selectedExpensesCode} onValueChange={(val) => updateQueryParams({ expensesCode: val })}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="U">En uso</SelectItem>
                  <SelectItem value="S">Perpetuidad</SelectItem>
                  <SelectItem value="N">N</SelectItem>
                  <SelectItem value="B">Urna cineraria</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-y-2">
              <span className="font-mono text-sm text-muted-foreground font-bold leading-none ml-1">Buscar</span>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Nombre o contrato..."
                    className="pl-8"
                    value={inputValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        handleSearch()
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-y-2 justify-end">
            <div className="flex items-center gap-2 justify-end">
              <Button onClick={handleSearch} variant="info" className="min-w-[110px]">Buscar</Button>
              <IconButton onClick={handleClearFilters} variant="default" title="Limpiar filtros" className="">
                <X className="h-4 w-4" />
              </IconButton>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className='flex flex-col gap-3'>
            <CardTitle>Parcelas {selectedFila !== "all" ? `Fila ${selectedFila}` : ""}</CardTitle>
            <CardDescription>
              Listado de parcelas con los filtros aplicados
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : !paginatedResponse || paginatedResponse.data.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <MapPin className="mx-auto h-12 w-12 mb-4 opacity-20" />
              <p>No se encontraron parcelas con los filtros aplicados.</p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={paginatedResponse.data}
              pagination={pagination}
              onPaginationChange={handlePaginationChange}
              pageCount={paginatedResponse.totalPages}
              totalRecords={paginatedResponse.total}
              onRowClick={(parcel) => parcel.contractId && navigateToContract(parcel.contractId!, parcel.id)}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Estado de Parcela</DialogTitle>
            <DialogDescription>
              Cambia el estado de la parcela #{selectedParcel?.parcela} en Sec. {selectedParcel?.section}, Fila {selectedParcel?.fila}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Estado:</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ParcelState.AVAILABLE}>Disponible</SelectItem>
                  <SelectItem value={ParcelState.RESERVED}>Reservado</SelectItem>
                  <SelectItem value={ParcelState.NOT_AVAILABLE}>No Disponible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground italic">
              * Nota: Los cambios de estado manuales deben ser realizados con precaución.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveStatus}
              disabled={updateStatusMutation.isPending || newStatus === selectedParcel?.state}
            >
              {updateStatusMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
